import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useDispatch } from "react-redux";

import { getOriginList } from "../../config";
import { getGameParentData, getPauseAfterPlayStatus } from "../../utilities/games";
import { setPrint, setSave } from "../../utilities/print";
import { sendResponse } from "../../utilities";
import routeCodes from "../../config/routeCodes";
import { setClickToPlayVideo, setMessageOrigin } from "../../redux/slices/preContentVideoSlice";
import {
  mpGameAbandoned,
  mpGameAnalytics,
  mpGameCompleted,
  mpGamePaused,
  mpGameResumed,
  mpGameStarted,
  mpGameToolbarAction,
  mpLevelCompleted,
  mpPlayPressed,
} from "../../utilities/mixpanel";
import { useGameState, useIsGameProxy } from "../../hooks";
import {
  setWasCompleted,
  setWasStarted,
  setGameActiveStartTime,
  setLevelTime,
  setLevelTotalDuration,
  setWasLevelStarted,
  setGameTotalDuration,
  setIsPaused,
  setGameHasCompletionState,
} from "../../redux/slices/gameSlice";

// using just for testing game events, remove when ready to PR to main
import { isTesting } from "../../utilities/detection";

export default function MessageHandler(props) {
  const dispatch = useDispatch();
  const location = useLocation();
  const gameState = useGameState();
  const listenerRef = useRef({ location, gameState });
  const isGameProxy = useIsGameProxy();

  useEffect(() => {
    if (isTesting() || process.env.NODE_ENV === "development") {
      console.log("gameState: ", JSON.stringify(gameState));
    }

    listenerRef.current = { location, gameState };
  }, [location, gameState]);

  const getListenerRef = () => {
    return listenerRef.current;
  };

  const getTimeSeconds = (endTime, startTime) => {
    return Math.floor((endTime - startTime) / 1000);
  };

  const tempTestingLogging = (message) => {
    // using just for testing game events, remove when ready to PR to main
    if (isTesting() || process.env.NODE_ENV === "development") {
      console.log(`%c GAME EVENT ==> ${message.type} message: ${JSON.stringify(message)}`, "font-weight: bold");
    }
  };

  const eventFunction = async (e) => {
    if (getOriginList().includes(e.origin)) {
      const { location, gameState } = getListenerRef();

      const {
        wasCompleted,
        wasStarted,
        wasLevelStarted,
        isPaused,
        gameTotalDuration,
        levelTotalDuration,
        gameActiveStartTime,
        levelTime,
        gameHasCompletionState,
        ...gameData
      } = gameState;

      const message = e[e.message ? "message" : "data"];

      if (message?.source === "game") {
        const type = message.type;
        const payload = message.payload || {};

        switch (type) {
          case "handshake": {
            message.payload = getGameParentData(location);
            message.status = "complete";
            sendResponse(message, e.origin);
            break;
          }

          case "playButtonPressed": {
            if (getPauseAfterPlayStatus(location)) {
              dispatch(setMessageOrigin({ messageOrigin: e.origin }));
              dispatch(setClickToPlayVideo({ clickToPlayVideo: true }));
            }
            if (isGameProxy) {
              break;
            }
            // using just for testing game events, remove when ready to PR to main
            tempTestingLogging(message);

            mpPlayPressed(gameData);
            break;
          }

          case "saveImage": {
            setSave({
              eventLabel: location.pathname,
              fileData: payload.data,
              fileName: payload.fileName,
              origin: e.origin,
              silent: message.silent,
              message: JSON.stringify(message),
            });
            break;
          }

          case "printImage": {
            setPrint({
              eventLabel: location.pathname,
              fileData: payload.data,
              fileName: payload.fileName,
              origin: e.origin,
              silent: message.silent,
              message: JSON.stringify(message),
            });
            break;
          }

          case "analytics": {
            if (location.pathname.startsWith(routeCodes.GAMEPROXY)) {
              const gameEmbeded = window.parent.location !== window.location;

              if (gameEmbeded) {
                // only if gameproxy is embeded in an iframe
                // parent is not itself
                // send message to parent embeding gameproxy page
                // currently just IXL domains
                window.parent.postMessage(message, "*");
              }
            }
            break;
          }

          case "gameStarted": {
            if (isGameProxy) {
              break;
            }
            // using just for testing game events, remove when ready to PR to main
            tempTestingLogging(message);

            const { isReplay, gameHasCompletionState } = payload;

            dispatch(setGameActiveStartTime({ gameActiveStartTime: Date.now() }));
            dispatch(setGameTotalDuration({ gameTotalDuration: 0 }));
            dispatch(setWasStarted({ wasStarted: true }));
            dispatch(setIsPaused({ isPaused: false }));
            dispatch(setGameHasCompletionState({ gameHasCompletionState }));

            mpGameStarted(gameData, isReplay, gameHasCompletionState, wasCompleted);
            break;
          }

          case "gameCompleted": {
            if (isGameProxy) {
              break;
            }
            // using just for testing game events, remove when ready to PR to main
            tempTestingLogging(message);

            const endTime = Date.now();
            const activePlayTimeSeconds = getTimeSeconds(endTime, gameActiveStartTime) + gameTotalDuration;
            const { completionOutcome } = payload;

            dispatch(setWasCompleted({ wasCompleted: true }));
            dispatch(setWasStarted({ wasStarted: false }));

            mpGameCompleted(gameData, activePlayTimeSeconds, wasCompleted, completionOutcome);
            break;
          }

          case "gamePaused": {
            if (isGameProxy) {
              break;
            }
            // using just for testing game events, remove when ready to PR to main
            tempTestingLogging(message);

            if (wasStarted && !isPaused) {
              const endTime = Date.now();
              const { pauseReason } = payload;

              dispatch(setIsPaused({ isPaused: true }));

              const totalDuration = getTimeSeconds(endTime, gameActiveStartTime) + gameTotalDuration;
              dispatch(setGameActiveStartTime({ gameActiveStartTime: endTime }));

              dispatch(setGameTotalDuration({ gameTotalDuration: totalDuration }));

              if (typeof wasLevelStarted === "number") {
                const totalLevelDuration =
                  getTimeSeconds(endTime, levelTime[wasLevelStarted]) + (levelTotalDuration[wasLevelStarted] || 0);

                dispatch(setLevelTotalDuration({ level: wasLevelStarted, levelTotalDuration: totalLevelDuration }));
              }

              mpGamePaused(gameData, pauseReason, gameHasCompletionState, wasCompleted);
            }
            break;
          }

          case "gameResumed": {
            if (isGameProxy) {
              break;
            }
            // using just for testing game events, remove when ready to PR to main
            tempTestingLogging(message);

            if (wasStarted && isPaused) {
              const endTime = Date.now();
              const timeElapsedSeconds = getTimeSeconds(endTime, gameActiveStartTime);

              dispatch(setIsPaused({ isPaused: false }));
              dispatch(setGameActiveStartTime({ gameActiveStartTime: endTime }));

              if (typeof wasLevelStarted === "number") {
                dispatch(setLevelTime({ level: wasLevelStarted, levelTime: endTime }));
              }

              mpGameResumed(gameData, timeElapsedSeconds, gameHasCompletionState, wasCompleted);
            }
            break;
          }

          case "gameAbandoned": {
            if (isGameProxy) {
              break;
            }
            // using just for testing game events, remove when ready to PR to main
            tempTestingLogging(message);

            const { exitMethod } = payload;
            const endTime = Date.now();

            if (exitMethod !== "inactivity") {
              dispatch(setWasStarted({ wasStarted: false }));

              if (typeof wasLevelStarted === "number" && !isPaused) {
                const totalLevelDuration =
                  getTimeSeconds(endTime, levelTime[wasLevelStarted]) + (levelTotalDuration[wasLevelStarted] || 0);

                dispatch(setLevelTotalDuration({ level: wasLevelStarted, levelTotalDuration: totalLevelDuration }));
              }

              dispatch(setWasLevelStarted({ wasLevelStarted: false }));
            }

            if (!wasCompleted && wasStarted) {
              const activePlayTimeSeconds = isPaused
                ? gameTotalDuration
                : getTimeSeconds(endTime, gameActiveStartTime) + gameTotalDuration;

              mpGameAbandoned(gameData, activePlayTimeSeconds, exitMethod, gameHasCompletionState, wasCompleted);
            }

            break;
          }

          case "levelStarted": {
            if (isGameProxy) {
              break;
            }
            // using just for testing game events, remove when ready to PR to main
            tempTestingLogging(message);

            const { level, reset } = payload;

            if (typeof wasLevelStarted === "number" && wasLevelStarted !== level && !isPaused) {
              // stop previous level
              const endTime = Date.now();
              const totalLevelDuration =
                getTimeSeconds(endTime, levelTime[wasLevelStarted]) + (levelTotalDuration[wasLevelStarted] || 0);

              dispatch(setLevelTotalDuration({ level: wasLevelStarted, levelTotalDuration: totalLevelDuration }));
            }

            if (reset) {
              dispatch(setLevelTotalDuration({ level, levelTotalDuration: 0 }));
            }

            if (reset || wasLevelStarted !== level) {
              // replay of failed level or manually reset
              dispatch(setLevelTime({ level, levelTime: Date.now() }));
            }

            dispatch(setWasLevelStarted({ wasLevelStarted: level }));
            break;
          }

          case "levelCompleted": {
            if (isGameProxy) {
              break;
            }
            // using just for testing game events, remove when ready to PR to main
            tempTestingLogging(message);

            const endTime = Date.now();
            const { level, totalGameLevels, attempts, completionOutcome } = payload;

            const activePlayTimeSeconds = getTimeSeconds(endTime, levelTime[level]) + (levelTotalDuration[level] || 0);

            dispatch(setWasLevelStarted({ wasLevelStarted: false }));

            mpLevelCompleted(
              gameData,
              activePlayTimeSeconds,
              level,
              totalGameLevels,
              attempts,
              wasCompleted,
              completionOutcome,
            );

            // reset level duration after a complete
            dispatch(setLevelTotalDuration({ level, levelTotalDuration: 0 }));
            break;
          }

          case "toolbarActionTriggered": {
            if (isGameProxy) {
              break;
            }
            // using just for testing game events, remove when ready to PR to main
            tempTestingLogging(message);

            const { actionType } = payload;
            mpGameToolbarAction(gameData, actionType);
            break;
          }

          case "gameAnalytics": {
            if (isGameProxy) {
              break;
            }
            // using just for testing game events, remove when ready to PR to main
            tempTestingLogging(message);

            const { event_name: eventName, props } = payload;
            mpGameAnalytics(gameData, eventName, props);
            break;
          }

          default: {
            message.status = "failed";
            message.statusMessage = "Unhandled message!";
            console.error("Unhandled message!", { type, payload });
            sendResponse(message, e.origin);
            break;
          }
        }
      }
    }
  };

  useEffect(() => {
    // listen for incoming events from games/media etc.
    window.addEventListener("message", eventFunction, false);

    return () => {
      // remove listener
      window.removeEventListener("message", eventFunction, false);
    };
  }, []);

  return props.children;
}
